import {
    CreateMcpServerRequest,
    PublishExtensionMetadata,
    UpdateMcpServerRequest,
} from '@shared/models/agentExtensions/AgentExtensionRequests';
import {
    McpRegistrationAuthType,
    McpRegistrationTransportType,
    McpServerRegistrationModel,
} from '@shared/models/agentExtensions/McpServerRegistrationModel';
import {
    CreateMcpServerInput,
    DEFAULT_MCP_ACTIVATION,
    McpAuthType,
    McpOauthRedirectType,
    McpServerModel,
    McpTransport,
} from '@shared/models/mcp/McpServerModel';

/**
 * Converts between the local runtime {@link McpServerModel} (string unions, discrete OAuth/apiKey
 * fields, secrets held separately) and the API {@link McpServerRegistrationModel} / request shapes
 * (`transportType`/`authType` enums + an `authConfigJson` blob).
 *
 * CRITICAL: only NON-SECRET configuration crosses this boundary. API keys, OAuth client secrets, OAuth
 * tokens, and secret header/env values are never read from the local model here (the redacted model
 * doesn't even carry them) and are never written back — a recipient re-enters their own credentials.
 */

const TRANSPORT_TO_WIRE: Record<McpTransport, McpRegistrationTransportType> = {
    http: 'Http',
    sse: 'Sse',
    stdio: 'Stdio',
};
const WIRE_TO_TRANSPORT: Record<McpRegistrationTransportType, McpTransport> = {
    Http: 'http',
    Sse: 'sse',
    Stdio: 'stdio',
};
const AUTH_TO_WIRE: Record<McpAuthType, McpRegistrationAuthType> = {
    none: 'None',
    apiKey: 'ApiKey',
    oauth: 'OAuth',
    headers: 'Custom',
};
const WIRE_TO_AUTH: Record<McpRegistrationAuthType, McpAuthType> = {
    None: 'none',
    ApiKey: 'apiKey',
    OAuth: 'oauth',
    Custom: 'headers',
};

/** Non-secret auth configuration shipped in `authConfigJson`. Never contains secret values. */
interface McpAuthConfig {
    apiKeyHeaderName?: string;
    apiKeyPrefix?: string;
    oauthClientId?: string;
    oauthScopes?: string[];
    oauthDiscoveryUrl?: string;
    oauthAuthorizationUrl?: string;
    oauthTokenUrl?: string;
    /** Which callback the provider redirects to — a property of the provider, so it travels with the config. */
    oauthRedirectType?: McpOauthRedirectType;
    oauthLocalhostPort?: number;
    /** Names of custom headers the recipient must supply (values are secret and not included). */
    headerNames?: string[];
}

function titleize(name: string): string {
    return name
        .split('-')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

/** Build the non-secret `authConfigJson` from a local server's auth fields. */
function buildAuthConfigJson(local: McpServerModel): string | undefined {
    const config: McpAuthConfig = {};
    if (local.authType === 'apiKey') {
        config.apiKeyHeaderName = local.apiKeyHeaderName;
        config.apiKeyPrefix = local.apiKeyPrefix;
    }
    if (local.authType === 'oauth') {
        config.oauthClientId = local.oauthClientId;
        config.oauthScopes = local.oauthScopes;
        config.oauthDiscoveryUrl = local.oauthDiscoveryUrl;
        config.oauthAuthorizationUrl = local.oauthAuthorizationUrl;
        config.oauthTokenUrl = local.oauthTokenUrl;
        config.oauthRedirectType = local.oauthRedirectType;
        // The port is only part of the URI a recipient has to register when the callback is loopback.
        if (local.oauthRedirectType === 'localhost') config.oauthLocalhostPort = local.oauthLocalhostPort;
    }
    if (local.headers) {
        config.headerNames = Object.keys(local.headers);
    }
    return Object.keys(config).length > 0 ? JSON.stringify(config) : undefined;
}

function parseAuthConfig(authConfigJson?: string): McpAuthConfig {
    if (!authConfigJson) return {};
    try {
        return JSON.parse(authConfigJson) as McpAuthConfig;
    } catch {
        return {};
    }
}

/**
 * Map a local server's NON-SECRET config to the API create-registration request. Author-edited
 * publishing `metadata` (display name, icons, categories, etc.) overrides the auto-derived defaults.
 */
export function localToCreateRequest(
    local: McpServerModel,
    organizationId?: string,
    metadata?: PublishExtensionMetadata
): CreateMcpServerRequest {
    const md = metadata ?? {};
    return {
        name: local.name,
        displayName: md.displayName?.trim() || titleize(local.name),
        description: md.description ?? local.description,
        shortDescription: md.shortDescription ?? local.description.slice(0, 140),
        iconUrl: md.iconUrl?.trim() || undefined,
        darkModeIconUrl: md.darkModeIconUrl?.trim() || undefined,
        categories: md.categories ?? [],
        version: md.version?.trim() || (local.remoteVersion ? String(local.remoteVersion) : '1'),
        serverUrl: local.url,
        transportType: TRANSPORT_TO_WIRE[local.transport],
        command: local.command,
        commandArgs: local.args,
        authType: AUTH_TO_WIRE[local.authType],
        authConfigJson: buildAuthConfigJson(local),
        organizationId,
    };
}

/** Map a local server's NON-SECRET config to the API update-registration request. */
export function localToUpdateRequest(
    local: McpServerModel,
    metadata?: PublishExtensionMetadata
): UpdateMcpServerRequest {
    const create = localToCreateRequest(local, undefined, metadata);
    // Update has the same shape minus organizationId.
    const { organizationId, ...rest } = create;
    void organizationId;
    return rest;
}

/**
 * Map an API registration to a local create input (NON-SECRET). The recipient adds their own
 * credentials afterward via the editor's OAuth/API-key flow.
 */
export function registrationToCreateInput(reg: McpServerRegistrationModel): CreateMcpServerInput {
    const auth = parseAuthConfig(reg.authConfigJson);
    return {
        name: reg.name,
        description: reg.description ?? '',
        transport: WIRE_TO_TRANSPORT[reg.transportType],
        command: reg.command,
        args: reg.commandArgs,
        url: reg.serverUrl,
        headers: auth.headerNames ? Object.fromEntries(auth.headerNames.map((h) => [h, ''])) : undefined,
        authType: WIRE_TO_AUTH[reg.authType],
        apiKeyHeaderName: auth.apiKeyHeaderName,
        apiKeyPrefix: auth.apiKeyPrefix,
        oauthClientId: auth.oauthClientId,
        oauthScopes: auth.oauthScopes,
        oauthDiscoveryUrl: auth.oauthDiscoveryUrl,
        oauthAuthorizationUrl: auth.oauthAuthorizationUrl,
        oauthTokenUrl: auth.oauthTokenUrl,
        oauthRedirectType: auth.oauthRedirectType,
        oauthLocalhostPort: auth.oauthLocalhostPort,
        activation: DEFAULT_MCP_ACTIVATION,
        requireApproval: true,
    };
}
